import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  LayoutDashboard, Wrench, UsersRound, Plug, Workflow, Settings, CreditCard, LogOut, ChevronRight, ChevronDown, Menu, X, Package, Layers,
  PauseCircle, Ban, Power, MapPinned, BarChart3, KeyRound, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePolling } from "@/hooks/usePolling";
import { getSolutionIcon } from "@/lib/solutionIcons";
import { NovaBrand } from "@/components/common/NovaBrand";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { ROLE_LABELS } from "@/lib/roles";

const STATUS_GATE_CONTENT = {
  Onboarding: {
    icon: Settings,
    iconBg: "bg-amber-100 dark:bg-amber-500/10",
    iconColor: "text-amber-600 dark:text-amber-400",
    heading: "Your organization is onboarding",
    message: (name) => `${name} is currently in onboarding and being reviewed by Nova Opsis. Access to the dashboard will unlock as soon as your organization is activated.`,
  },
  Paused: {
    icon: PauseCircle,
    iconBg: "bg-blue-100 dark:bg-blue-500/10",
    iconColor: "text-blue-600 dark:text-blue-400",
    heading: "Your organization is paused",
    message: (name) => `${name} is currently paused by Nova Opsis. Access to the dashboard will unlock once your organization is reactivated.`,
  },
  Suspended: {
    icon: Ban,
    iconBg: "bg-red-100 dark:bg-red-500/10",
    iconColor: "text-red-600 dark:text-red-400",
    heading: "Your organization is suspended",
    message: (name) => `${name} is currently suspended by Nova Opsis. Access to the dashboard will unlock once your organization is reactivated.`,
  },
  Inactive: {
    icon: Power,
    iconBg: "bg-slate-100 dark:bg-white/10",
    iconColor: "text-slate-500 dark:text-slate-400",
    heading: "Your organization is inactive",
    message: (name) => `${name} is currently inactive. Access to the dashboard will unlock once your organization is reactivated by Nova Opsis.`,
  },
};

const BUSINESS_ITEMS = [
  { label: "Services", path: "/dashboard/services", icon: Wrench },
  { label: "Products", path: "/dashboard/products", icon: Package },
  { label: "Bundles", path: "/dashboard/bundles", icon: Layers },
  { label: "Credentials", path: "/dashboard/credentials", icon: KeyRound },
];
const TEAM_ITEM = { label: "Team & Access", path: "/dashboard/team", icon: UsersRound };
const SERVICE_AREAS_ITEM = { label: "Service Areas", path: "/dashboard/service-areas", icon: MapPinned };
const OWNER_ONLY_ITEMS = [
  { label: "Connections", path: "/dashboard/connections", icon: Plug },
  { label: "Automations", path: "/dashboard/workflows", icon: Workflow },
  { label: "Organization Settings", path: "/dashboard/settings", icon: Settings },
];
const BILLING_ITEM = { label: "Nova Plan & Billing", path: "/dashboard/billing", icon: CreditCard };
const navClass = (isActive) =>
  `${
    isActive
      ? "bg-gradient-to-r from-[#0052FF] to-[#7B61FF] text-white"
      : "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white"
  }`;

const IconTooltip = ({ label, anchorRef, show }) => {
  if (!show || !anchorRef.current) return null;
  const rect = anchorRef.current.getBoundingClientRect();
  return createPortal(
    <span
      className="pointer-events-none fixed z-[100] whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white shadow-lg dark:bg-white dark:text-slate-900"
      style={{ left: rect.right + 10, top: rect.top + rect.height / 2, transform: "translateY(-50%)" }}
      data-testid="org-sidebar-tooltip"
    >
      {label}
    </span>,
    document.body,
  );
};

const SidebarLink = ({ to, end, icon: Icon, label, collapsed, onNavigate, testId, badge, chevron }) => {
  const [hover, setHover] = useState(false);
  const ref = useRef(null);
  return (
    <NavLink
      ref={ref}
      to={to}
      end={end}
      onClick={onNavigate}
      onMouseEnter={() => collapsed && setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={testId}
      className={({ isActive }) =>
        `relative flex items-center rounded-xl py-2.5 text-sm font-semibold transition-colors duration-200 ${collapsed ? "justify-center px-0" : "gap-3 px-3.5"} ${navClass(isActive)}`
      }
    >
      <span className="relative flex shrink-0 items-center justify-center">
        <Icon size={18} strokeWidth={1.8} />
        {collapsed && !!badge && (
          <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white" data-testid={`${testId}-badge`}>{badge}</span>
        )}
      </span>
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
      {!collapsed && !!badge && (
        <span className="rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid={`${testId}-badge`}>{badge}</span>
      )}
      {!collapsed && chevron && <ChevronRight size={14} className="opacity-50" />}
      <IconTooltip label={label} anchorRef={ref} show={collapsed && hover} />
    </NavLink>
  );
};

const SidebarContent = ({ org, isOwner, isManager, hasNovaBilling, solutions, roleLabel, teamNames, frontDeskBadge, onNavigate, collapsed }) => {
  return (
    <div className="flex h-full flex-col bg-white text-slate-900 dark:bg-[#000103] dark:text-white">
      <div className={`border-b border-slate-200 dark:border-white/10 ${collapsed ? "px-3 py-5" : "px-6 py-6"}`}>
        <div className={`flex items-center ${collapsed ? "justify-center" : ""}`}>
          {collapsed ? (
            <img src="/nova-icon-official.png" alt="Nova Opsis" className="h-8 w-8 shrink-0" data-testid="nova-brand-icon" />
          ) : (
            <NovaBrand className="w-full" />
          )}
        </div>
        {!collapsed && (
          <>
            <p className="mt-4 px-0.5 text-[10px] font-bold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300" data-testid="org-role-label">{roleLabel}</p>

            {teamNames.length > 0 && (
              <p className="mt-0.5 px-0.5 text-xs font-semibold text-slate-500 dark:text-slate-400" data-testid="org-sidebar-team-name">{teamNames.join(", ")}</p>
            )}
          </>
        )}
      </div>

      <nav className={`flex-1 overflow-y-auto ${collapsed ? "mt-4 space-y-4 px-2" : "mt-6 space-y-6 px-5"}`} data-testid="org-sidebar-nav">
        <div className="space-y-1">
          <SidebarLink to="/dashboard" end icon={LayoutDashboard} label="Overview" collapsed={collapsed} onNavigate={onNavigate} testId="org-nav-overview" />
          <SidebarLink to="/dashboard/analytics" icon={BarChart3} label="Analytics" collapsed={collapsed} onNavigate={onNavigate} testId="org-nav-analytics" />
        </div>

        {solutions.length > 0 && (
          <div className={collapsed ? "border-t border-slate-100 pt-4 dark:border-white/5" : ""}>
            {!collapsed && <p className="px-3 text-[10px] font-bold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">Solutions</p>}
            <div className={`space-y-1 ${collapsed ? "" : "mt-2"}`}>
              {solutions.map((s) => {
                const Icon = getSolutionIcon(s.icon);
                return (
                  <SidebarLink
                    key={s.solution_id}
                    to={`/dashboard/solutions/${s.slug}`}
                    icon={Icon}
                    label={s.name}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                    testId={`org-nav-solution-${s.slug}`}
                    badge={s.slug === "front-desk" ? frontDeskBadge : 0}
                    chevron
                  />
                );
              })}
            </div>
          </div>
        )}

        <div className={collapsed ? "border-t border-slate-100 pt-4 dark:border-white/5" : ""}>
          {!collapsed && <p className="px-3 text-[10px] font-bold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">Business</p>}
          <div className={`space-y-1 ${collapsed ? "" : "mt-2"}`}>
            {BUSINESS_ITEMS.map((item) => (
              <SidebarLink key={item.path} to={item.path} icon={item.icon} label={item.label} collapsed={collapsed} onNavigate={onNavigate} testId={`org-nav-${item.label.toLowerCase()}`} />
            ))}
          </div>
        </div>

        {(isManager || hasNovaBilling) && (
          <div className={collapsed ? "border-t border-slate-100 pt-4 dark:border-white/5" : ""}>
            {!collapsed && <p className="px-3 text-[10px] font-bold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">Manage</p>}
            <div className={`space-y-1 ${collapsed ? "" : "mt-2"}`}>
              {isManager && (
                <SidebarLink to={TEAM_ITEM.path} icon={TEAM_ITEM.icon} label={TEAM_ITEM.label} collapsed={collapsed} onNavigate={onNavigate} testId="org-nav-team-access" />
              )}
              {isManager && (
                <SidebarLink to={SERVICE_AREAS_ITEM.path} icon={SERVICE_AREAS_ITEM.icon} label={SERVICE_AREAS_ITEM.label} collapsed={collapsed} onNavigate={onNavigate} testId="org-nav-service-areas" />
              )}
              {isOwner && OWNER_ONLY_ITEMS.map((item) => (
                <SidebarLink key={item.path} to={item.path} icon={item.icon} label={item.label} collapsed={collapsed} onNavigate={onNavigate} testId={`org-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
              ))}
              {hasNovaBilling && (
                <SidebarLink to={BILLING_ITEM.path} icon={BILLING_ITEM.icon} label={BILLING_ITEM.label} collapsed={collapsed} onNavigate={onNavigate} testId="org-nav-nova-plan-billing" />
              )}
            </div>
          </div>
        )}
      </nav>

      {!collapsed && (
        <div className="px-5 pb-6 pt-4">
          <p className="text-center text-[10px] text-slate-400 dark:text-slate-600">© 2026 Nova Opsis. All rights reserved.</p>
        </div>
      )}
    </div>
  );
};

export default function OrgOwnerLayout() {
  const { user, logout, API, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [solutions, setSolutions] = useState([]);
  const [frontDeskBadge, setFrontDeskBadge] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [accessEmail, setAccessEmail] = useState("");
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("nova_sidebar_collapsed") === "1");
  const isOwner = user?.role === "org_owner";
  const isManager = isOwner || user?.role === "org_admin";
  const hasNovaBilling = !!user?.nova_billing_access;
  const org = user?.organization;

  useEffect(() => { localStorage.setItem("nova_sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  const toggleCollapsed = () => setCollapsed((value) => !value);

  useEffect(() => {
    if (!user?.org_id) return;
    axios.get(`${API}/org/solutions`, { withCredentials: true })
      .then(({ data }) => setSolutions(data.solutions || []))
      .catch(() => setSolutions([]));
  }, [API, user?.org_id]);

  useEffect(() => { setMobileOpen(false); setProfileOpen(false); }, [location.pathname]);

  const hasFrontDesk = solutions.some((s) => s.slug === "front-desk");
  const refreshFrontDeskBadge = () => {
    if (!hasFrontDesk) return;
    axios.get(`${API}/org/front-desk/badge`, { withCredentials: true })
      .then(({ data }) => setFrontDeskBadge(data.new_submissions || 0))
      .catch(() => setFrontDeskBadge(0));
  };
  useEffect(() => { refreshFrontDeskBadge(); }, [API, hasFrontDesk]);
  usePolling(refreshFrontDeskBadge, 20000);

  // Re-validate the organization's current status on every navigation, so a status change
  // made by a Platform Admin mid-session takes effect without requiring the user to log out.
  useEffect(() => { checkAuth(); }, [location.pathname, checkAuth]);
  usePolling(checkAuth, 20000);

  useEffect(() => {
    if (org?.status && org.status !== "Active") {
      axios.get(`${API}/platform-emails/public`)
        .then(({ data }) => setAccessEmail(data.access_email || ""))
        .catch(() => setAccessEmail(""));
    }
  }, [API, org?.status]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const initials = (user?.name || user?.email || "U")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const roleLabel = ROLE_LABELS[user?.role] || "Organization User";
  const teamNames = (user?.teams || []).map((t) => t.name).filter(Boolean);

  if (!user?.org_id) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-center" data-testid="org-workspace-no-org">
        <p className="text-sm font-semibold text-slate-600">This account is not linked to an organization.</p>
      </div>
    );
  }

  if (org?.status !== "Active") {
    const screen = STATUS_GATE_CONTENT[org?.status] || STATUS_GATE_CONTENT.Onboarding;
    const Icon = screen.icon;
    const orgName = org?.name || "Your organization";
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC] p-6 text-center dark:bg-[#0B1220]" data-testid="org-status-gate-screen">
        <div className="max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-sm dark:border-white/10 dark:bg-[#111C33]">
          <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${screen.iconBg}`} data-testid="org-status-gate-icon">
            <Icon size={26} className={screen.iconColor} strokeWidth={1.8} />
          </div>
          <h1 className="text-lg font-bold text-slate-950 dark:text-white" data-testid="org-status-gate-heading">{screen.heading}</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400" data-testid="org-status-gate-message">
            {screen.message(orgName)}
          </p>
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400" data-testid="org-status-gate-assistance">
            Feel free to reach out to{" "}
            {accessEmail ? (
              <a href={`mailto:${accessEmail}`} className="font-semibold text-blue-600 hover:underline dark:text-blue-400" data-testid="org-status-gate-support-email">
                {accessEmail}
              </a>
            ) : (
              <span className="font-semibold" data-testid="org-status-gate-support-email">our support team</span>
            )}{" "}
            for further assistance.
          </p>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="org-status-gate-signout-btn"
            className="mt-6 inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:bg-slate-100 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-950 dark:bg-[#0B1220] dark:text-white" data-testid="org-owner-portal">
      <aside className={`fixed inset-y-0 left-0 z-40 hidden transition-[width] duration-200 lg:block ${collapsed ? "w-20" : "w-72"}`}>
        <SidebarContent org={org} isOwner={isOwner} isManager={isManager} hasNovaBilling={hasNovaBilling} solutions={solutions} roleLabel={roleLabel} teamNames={teamNames} frontDeskBadge={frontDeskBadge} onNavigate={() => {}} collapsed={collapsed} />
        <button
          type="button"
          onClick={toggleCollapsed}
          data-testid="org-sidebar-collapse-toggle"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="group absolute -right-3 top-44 z-50 hidden h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 shadow-md transition-colors duration-200 hover:bg-slate-100 hover:text-slate-700 dark:border-white/15 dark:bg-[#0B1220] dark:text-slate-500 dark:hover:bg-white/10 dark:hover:text-white lg:flex"
        >
          {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
          <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs font-semibold text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 dark:bg-white dark:text-slate-900">
            {collapsed ? "Expand sidebar" : "Collapse sidebar"}
          </span>
        </button>
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <motion.button
              type="button"
              aria-label="Close menu"
              className="absolute inset-0 bg-slate-950/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              className="absolute inset-y-0 left-0 w-72"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              <SidebarContent org={org} isOwner={isOwner} isManager={isManager} hasNovaBilling={hasNovaBilling} solutions={solutions} roleLabel={roleLabel} teamNames={teamNames} frontDeskBadge={frontDeskBadge} onNavigate={() => setMobileOpen(false)} collapsed={false} />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className={`transition-[padding] duration-200 ${collapsed ? "lg:pl-20" : "lg:pl-72"}`}>
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/92 backdrop-blur-xl dark:border-white/10 dark:bg-[#0B1220]/90">
          <div className="flex min-h-[76px] items-center justify-between gap-4 px-5 sm:px-7 lg:px-8">
            <button type="button" onClick={() => setMobileOpen(true)} data-testid="org-mobile-menu-btn" className="rounded-xl border border-slate-200 p-2 text-slate-600 lg:hidden dark:border-white/15 dark:text-slate-200">
              <Menu size={18} />
            </button>
            {org?.logo_url ? (
              <div className="hidden items-center gap-3 sm:flex">
                <img src={org.logo_url} alt="" className="h-12 w-auto object-contain" data-testid="org-header-logo" />
                <p className="text-sm font-semibold text-slate-500 dark:text-slate-400" data-testid="org-header-org-name">{org?.name || "Organization"}</p>
              </div>
            ) : (
              <p className="hidden text-sm font-semibold text-slate-500 sm:block dark:text-slate-400" data-testid="org-header-org-name">{org?.name || "Organization"}</p>
            )}
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setProfileOpen((value) => !value)}
                  data-testid="org-profile-menu-btn"
                  className="flex items-center gap-3 rounded-xl px-2 py-1.5 transition-colors duration-200 hover:bg-slate-100 dark:hover:bg-white/10"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#0052FF] to-[#00D4FF] text-xs font-extrabold text-white">
                      {initials}
                    </span>
                  )}
                  <span className="hidden text-left sm:block">
                    <span className="block text-sm font-bold text-slate-950 dark:text-white" data-testid="org-profile-name">{user?.name || user?.email}</span>
                    <span className="block text-xs text-slate-500 dark:text-slate-400">{roleLabel}</span>
                    {teamNames.length > 0 && (
                      <span className="block text-xs text-slate-400 dark:text-slate-500" data-testid="org-profile-team-name">{teamNames.join(", ")}</span>
                    )}
                  </span>
                  <ChevronDown size={16} className="text-slate-400 dark:text-slate-500" />
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg dark:border-white/10 dark:bg-[#111C33]" data-testid="org-profile-menu">
                    <div className="border-b border-slate-100 px-3 py-3 dark:border-white/10">
                      <p className="text-sm font-bold text-slate-950 dark:text-white">{user?.name || roleLabel}</p>
                      <p className="mt-0.5 break-all text-xs text-slate-500 dark:text-slate-400" data-testid="org-profile-email">{user?.email}</p>
                      <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">{roleLabel}</p>
                      {teamNames.length > 0 && (
                        <p className="text-xs text-slate-400 dark:text-slate-500" data-testid="org-profile-menu-team-name">{teamNames.join(", ")}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleLogout}
                      data-testid="org-sign-out-btn"
                      className="mt-1 flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-red-600 transition-colors duration-200 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                    >
                      <LogOut size={16} />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        <main className="p-5 sm:p-7 lg:p-8" data-testid="org-main-content">
          {user?.incomplete_access && (
            <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-400" data-testid="org-incomplete-access-banner">
              Your account has incomplete access. Ask your organization owner or admin to assign you to a team to unlock your Solutions.
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
